import { createSignal } from "solid-js";

export interface SearchStore {
  isOpen: () => boolean;
  query: () => string;
  debouncedQuery: () => string;
  caseSensitive: () => boolean;
  regex: () => boolean;
  wholeWord: () => boolean;
  currentIndex: () => number;

  open: () => void;
  close: () => void;
  setQuery: (s: string) => void;
  toggleCase: () => void;
  toggleRegex: () => void;
  toggleWholeWord: () => void;
  setTotal: (n: number) => void;
  next: () => void;
  prev: () => void;
  resetForSnapshot: () => void;
}

const DEBOUNCE_MS = 80;

export function createSearchStore(): SearchStore {
  const [isOpen, setIsOpen] = createSignal(false);
  const [query, setQuerySig] = createSignal("");
  const [debouncedQuery, setDebouncedQuery] = createSignal("");
  const [caseSensitive, setCaseSensitive] = createSignal(false);
  const [regex, setRegex] = createSignal(false);
  const [wholeWord, setWholeWord] = createSignal(false);
  const [currentIndex, setCurrentIndex] = createSignal(0);
  const [total, setTotalSig] = createSignal(0);

  let timer: ReturnType<typeof setTimeout> | null = null;
  const clearTimer = () => {
    if (timer != null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const flushDebounced = (v: string) => {
    setDebouncedQuery(v);
    setCurrentIndex(0);
  };

  const setQuery = (s: string) => {
    setQuerySig(s);
    clearTimer();
    if (s === "") {
      flushDebounced("");
      return;
    }
    timer = setTimeout(() => {
      timer = null;
      flushDebounced(s);
    }, DEBOUNCE_MS);
  };

  const toggleCase = () => {
    setCaseSensitive((v) => !v);
    setCurrentIndex(0);
  };

  const toggleRegex = () => {
    setRegex((v) => !v);
    setCurrentIndex(0);
  };

  const toggleWholeWord = () => {
    setWholeWord((v) => !v);
    setCurrentIndex(0);
  };

  const setTotal = (n: number) => {
    setTotalSig(n);
    setCurrentIndex((i) => (n === 0 ? 0 : Math.min(i, n - 1)));
  };

  const next = () => {
    const t = total();
    if (t > 0) setCurrentIndex((i) => (i + 1) % t);
  };
  const prev = () => {
    const t = total();
    if (t > 0) setCurrentIndex((i) => (i - 1 + t) % t);
  };

  const resetForSnapshot = () => {
    clearTimer();
    setIsOpen(false);
    setQuerySig("");
    setDebouncedQuery("");
    setCaseSensitive(false);
    setRegex(false);
    setWholeWord(false);
    setCurrentIndex(0);
    setTotalSig(0);
  };

  return {
    isOpen,
    query,
    debouncedQuery,
    caseSensitive,
    regex,
    wholeWord,
    currentIndex,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    setQuery,
    toggleCase,
    toggleRegex,
    toggleWholeWord,
    setTotal,
    next,
    prev,
    resetForSnapshot,
  };
}
